import { useState, useMemo, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { calculateTimeBlocks, calculateTotalWorkMinutes, calculateBudgetConsumption } from '../utils/timeCalculator';
import { SharedBudgetCard } from './SharedBudgetCard';
import { BudgetEditModal } from './BudgetEditModal';
import type { ProjectBudget, Position, Profile, TimeEntry } from '../types';
import {
  Target,
  TrendingUp,
  AlertCircle,
  Plus,
  Layers,
  Search,
  X,
  ChevronsUpDown,
  LayoutGrid,
  List,
  Building2,
  Briefcase,
  FileText,
  FolderTree,
  ChevronDown,
  Users
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string | number | undefined;
  onChange: (value: string) => void;
}

function DebouncedInput({ value, onChange, ...props }: DebouncedInputProps) {
  const [localVal, setLocalVal] = useState(value?.toString() || '');
  const [prevVal, setPrevVal] = useState(value);
  const [isTyping, setIsTyping] = useState(false);

  if (value !== prevVal) {
    setPrevVal(value);
    setLocalVal(value?.toString() || '');
  }

  useEffect(() => {
    if (isTyping) {
      const handler = setTimeout(() => {
        onChange(localVal);
        setIsTyping(false);
      }, 800);
      return () => clearTimeout(handler);
    }
  }, [localVal, isTyping, onChange]);

  return (
    <input
      {...props}
      value={localVal}
      onChange={e => {
        setLocalVal(e.target.value);
        setIsTyping(true);
      }}
      onBlur={() => {
        if (isTyping) {
          onChange(localVal);
          setIsTyping(false);
        }
      }}
    />
  );
}

export function EffortTracker() {
  const {
    projects,
    entries,
    projectBudgets,
    userProjectMonthlyPlans,
    monthlyTargets,
    updateUserProjectMonthlyPlan,
    updateMonthlyTarget,
    saveProjectBudget,
    deleteProjectBudget,
    fetchBudgetEntries,
    fetchProfiles
  } = useData();

  const { user, role, isSupervisorMode } = useAuth();
  const isSupervisor = typeof isSupervisorMode === 'boolean' ? isSupervisorMode : role === 'supervisor';

  const userEntries = useMemo(() => {
    return user?.id ? entries.filter(e => !e.userId || e.userId === user.id) : entries;
  }, [entries, user]);

  const [selectedProjectId, setSelectedProjectId] = useState<string>('total');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [budgetEntries, setBudgetEntries] = useState<TimeEntry[]>([]);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<ProjectBudget | null>(null);

  // Dense overview & collapsible controls
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'attention' | 'on-track'>('all');
  const [layoutMode, setLayoutMode] = useState<'list' | 'grid'>('list');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [collapsedUserIds, setCollapsedUserIds] = useState<Set<string>>(() => new Set());
  type GroupByOption = 'none' | 'customerId' | 'serviceType' | 'contract';
  const [groupBy, setGroupBy] = useState<GroupByOption>('none');
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<Set<string>>(() => new Set());
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState<string>('all');

  // Sorted employee profiles for supervisor filtering
  const sortedProfiles = useMemo(() => {
    return [...profiles].sort((a, b) => {
      const nameA = [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email;
      const nameB = [b.firstName, b.lastName].filter(Boolean).join(' ') || b.email;
      return nameA.localeCompare(nameB);
    });
  }, [profiles]);

  // Load profiles for budget members
  useEffect(() => {
    const loadProfiles = async () => {
      try {
        const data = await fetchProfiles();
        setProfiles(data);
      } catch (err) {
        console.error('Failed to load profiles:', err);
      }
    };
    loadProfiles();
  }, [fetchProfiles]);

  // Load team entries for active budgets
  useEffect(() => {
    const loadAllBudgetEntries = async () => {
      let visibleBudgets: ProjectBudget[] = [];
      if (isSupervisor) {
        visibleBudgets = projectBudgets;
      } else if (user?.id) {
        visibleBudgets = projectBudgets.filter(b => b.isGlobal || b.members?.includes(user.id));
      }

      if (visibleBudgets.length === 0) {
        setBudgetEntries([]);
        return;
      }
      try {
        // Find earliest start date and latest end date
        let minDate: string | undefined = undefined;
        let maxDate: string | undefined = undefined;
        for (const b of visibleBudgets) {
          if (b.startDate && (!minDate || b.startDate < minDate)) minDate = b.startDate;
          if (b.endDate && (!maxDate || b.endDate > maxDate)) maxDate = b.endDate;
        }

        const teamEntries = await fetchBudgetEntries('', minDate, maxDate);
        setBudgetEntries(teamEntries);
      } catch (err) {
        console.error('Failed to load team budget entries:', err);
      }
    };
    loadAllBudgetEntries();
  }, [projectBudgets, isSupervisor, user, fetchBudgetEntries]);

  // Combined entries (user entries + any team budget entries for calculations)
  const allAvailableEntries = useMemo(() => {
    const entryMap = new Map<string, TimeEntry>();
    entries.forEach(e => entryMap.set(e.id, e));
    budgetEntries.forEach(e => entryMap.set(e.id, e));
    return Array.from(entryMap.values());
  }, [entries, budgetEntries]);

  // Filter project budgets relevant to current view
  const relevantBudgets = useMemo(() => {
    let visibleBudgets: ProjectBudget[] = [];
    if (isSupervisor) {
      visibleBudgets = projectBudgets;
    } else if (user?.id) {
      visibleBudgets = projectBudgets.filter(b => b.isGlobal || b.members?.includes(user.id));
    }

    if (isSupervisor || selectedProjectId === 'total') {
      return visibleBudgets;
    }
    return visibleBudgets.filter(b => (b.projectId || b.id) === selectedProjectId);
  }, [projectBudgets, selectedProjectId, isSupervisor, user]);

  // Supervisor aggregate metrics
  const supervisorOverview = useMemo(() => {
    if (!isSupervisor) return null;

    const totalPositions = relevantBudgets.length;
    let totalContingentDays = 0;
    let totalBookedDays = 0;
    let criticalCount = 0;
    let warningCount = 0;
    let normalCount = 0;

    relevantBudgets.forEach(b => {
      const stats = calculateBudgetConsumption(allAvailableEntries, b);
      const poolDays = b.contingentDays ?? b.budgetDays ?? 0;
      totalContingentDays += poolDays;
      totalBookedDays += stats.totalDays;

      if (poolDays > 0) {
        if (stats.teamThresholdStatus === 'critical') criticalCount++;
        else if (stats.teamThresholdStatus === 'warning') warningCount++;
        else normalCount++;
      } else {
        normalCount++;
      }
    });

    const needsAttentionCount = criticalCount + warningCount;

    return {
      totalPositions,
      totalContingentDays,
      totalBookedDays,
      criticalCount,
      warningCount,
      normalCount,
      needsAttentionCount
    };
  }, [isSupervisor, relevantBudgets, allAvailableEntries]);

  // Filtered positions based on search and status
  const filteredBudgets = useMemo(() => {
    return relevantBudgets.filter(b => {
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const name = (projects.find(p => p.id === (b.projectId || b.id))?.name || b.name || '').toLowerCase();
        const contractId = (b.contractId || '').toLowerCase();
        const customerId = (b.customerId || '').toLowerCase();
        const customerDepartmentId = (b.customerDepartmentId || '').toLowerCase();
        const serviceType = (b.serviceType || '').toLowerCase();
        const contract = (b.contract || '').toLowerCase();
        if (
          !name.includes(query) &&
          !contractId.includes(query) &&
          !customerId.includes(query) &&
          !customerDepartmentId.includes(query) &&
          !serviceType.includes(query) &&
          !contract.includes(query)
        ) {
          return false;
        }
      }

      if (isSupervisor && statusFilter !== 'all') {
        const stats = calculateBudgetConsumption(allAvailableEntries, b);
        const poolDays = b.contingentDays ?? b.budgetDays ?? 0;
        if (statusFilter === 'attention') {
          if (poolDays <= 0 || (stats.teamThresholdStatus !== 'warning' && stats.teamThresholdStatus !== 'critical')) {
            return false;
          }
        } else if (statusFilter === 'on-track') {
          if (poolDays > 0 && stats.teamThresholdStatus !== 'normal') {
            return false;
          }
        }
      }

      if (isSupervisor && selectedEmployeeFilter !== 'all') {
        const isMember = Boolean(b.members?.includes(selectedEmployeeFilter));
        const stats = calculateBudgetConsumption(allAvailableEntries, b);
        const hasBooked = (stats.memberDays[selectedEmployeeFilter] || 0) > 0;
        const isGlobal = Boolean(b.isGlobal);

        if (!isMember && !hasBooked && !isGlobal) {
          return false;
        }
      }

      return true;
    });
  }, [relevantBudgets, searchQuery, statusFilter, selectedEmployeeFilter, isSupervisor, allAvailableEntries, projects]);

  // Grouping computation
  const groupedBudgets = useMemo(() => {
    if (groupBy === 'none') return [];

    const groupMap = new Map<string, Position[]>();

    filteredBudgets.forEach(b => {
      let rawVal = '';
      if (groupBy === 'customerId') rawVal = b.customerId?.trim() || '';
      else if (groupBy === 'serviceType') rawVal = b.serviceType?.trim() || '';
      else if (groupBy === 'contract') rawVal = b.contract?.trim() || '';

      const key = rawVal || '__unassigned__';
      const list = groupMap.get(key) || [];
      list.push(b);
      groupMap.set(key, list);
    });

    const groups: {
      key: string;
      label: string;
      field: 'none' | 'customerId' | 'serviceType' | 'contract';
      budgets: Position[];
      totalContingentDays: number;
      totalBookedDays: number;
    }[] = [];

    const unassignedGroup = groupMap.has('__unassigned__') ? {
      key: '__unassigned__',
      label: groupBy === 'customerId' ? 'No Customer Name' : groupBy === 'serviceType' ? 'No Service Type' : 'No SPF Contract ID',
      field: groupBy,
      budgets: groupMap.get('__unassigned__')!,
      totalContingentDays: 0,
      totalBookedDays: 0
    } : null;

    // Sort named groups alphabetically
    const sortedKeys = Array.from(groupMap.keys())
      .filter(k => k !== '__unassigned__')
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

    sortedKeys.forEach(k => {
      const bList = groupMap.get(k)!;
      let contingent = 0;
      let booked = 0;
      bList.forEach(b => {
        contingent += b.contingentDays ?? b.budgetDays ?? 0;
        const stats = calculateBudgetConsumption(allAvailableEntries, b);
        booked += stats.totalDays;
      });

      groups.push({
        key: k,
        label: k,
        field: groupBy,
        budgets: bList,
        totalContingentDays: contingent,
        totalBookedDays: booked
      });
    });

    if (unassignedGroup) {
      let contingent = 0;
      let booked = 0;
      unassignedGroup.budgets.forEach(b => {
        contingent += b.contingentDays ?? b.budgetDays ?? 0;
        const stats = calculateBudgetConsumption(allAvailableEntries, b);
        booked += stats.totalDays;
      });
      unassignedGroup.totalContingentDays = contingent;
      unassignedGroup.totalBookedDays = booked;
      groups.push(unassignedGroup);
    }

    return groups;
  }, [filteredBudgets, groupBy, allAvailableEntries]);

  const handleGroupByChange = (field: 'none' | 'customerId' | 'serviceType' | 'contract') => {
    setGroupBy(prev => {
      const next = prev === field ? 'none' : field;
      setCollapsedGroupKeys(new Set());
      return next;
    });
  };

  const toggleGroupCollapse = (key: string) => {
    setCollapsedGroupKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isPositionExpanded = useCallback((id: string) => {
    if (isSupervisor) {
      return expandedIds.has(id);
    }
    return !collapsedUserIds.has(id);
  }, [isSupervisor, expandedIds, collapsedUserIds]);

  const handleTogglePosition = (id: string) => {
    if (isSupervisor) {
      setExpandedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setCollapsedUserIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  };

  const allExpanded = useMemo(() => {
    if (filteredBudgets.length === 0) return false;
    if (groupBy !== 'none') {
      const allGroupsOpen = groupedBudgets.every(g => !collapsedGroupKeys.has(g.key));
      const allPositionsOpen = filteredBudgets.every(b => isPositionExpanded(b.id));
      return allGroupsOpen && allPositionsOpen;
    }
    return filteredBudgets.every(b => isPositionExpanded(b.id));
  }, [filteredBudgets, isPositionExpanded, groupBy, groupedBudgets, collapsedGroupKeys]);

  const handleToggleAll = () => {
    if (allExpanded) {
      if (groupBy !== 'none') {
        setCollapsedGroupKeys(new Set(groupedBudgets.map(g => g.key)));
      }
      if (isSupervisor) {
        setExpandedIds(new Set());
      } else {
        setCollapsedUserIds(new Set(filteredBudgets.map(b => b.id)));
      }
    } else {
      if (groupBy !== 'none') {
        setCollapsedGroupKeys(new Set());
      }
      if (isSupervisor) {
        setExpandedIds(new Set(filteredBudgets.map(b => b.id)));
      } else {
        setCollapsedUserIds(new Set());
      }
    }
  };

  // Generate all 12 months for any year that has data, plus the current year
  const months = useMemo(() => {
    const years = new Set<string>();
    years.add(new Date().getFullYear().toString());

    userEntries.forEach(e => years.add(e.date.substring(0, 4)));
    userProjectMonthlyPlans.forEach(b => years.add(b.month.substring(0, 4)));
    monthlyTargets.forEach(t => years.add(t.month.substring(0, 4)));

    const allMonths: string[] = [];
    Array.from(years).forEach(year => {
      for (let i = 1; i <= 12; i++) {
        allMonths.push(`${year}-${i.toString().padStart(2, '0')}`);
      }
    });
    return allMonths.sort();
  }, [userEntries, userProjectMonthlyPlans, monthlyTargets]);

  // Pre-calculate actual minutes per project per month
  const actualsByProjectAndMonth = useMemo(() => {
    const acc: Record<string, Record<string, number>> = {};
    for (const entry of userEntries) {
      const month = entry.date.substring(0, 7);
      const blocks = calculateTimeBlocks(
        entry.timeArrived,
        entry.timeLeft,
        entry.breakDurationMinutes,
        entry.projects,
        projects
      );

      for (const b of blocks) {
        if (!acc[b.projectId]) acc[b.projectId] = {};
        if (!acc[b.projectId][month]) acc[b.projectId][month] = 0;

        const mins = typeof b.workMinutes === 'number' && b.workMinutes > 0
          ? b.workMinutes
          : calculateTotalWorkMinutes(b.timeArrived, b.timeLeft, b.breakDurationMinutes);
        acc[b.projectId][month] += Math.max(0, mins);
      }
    }
    return acc;
  }, [userEntries, projects]);

  const handleBudgetChange = async (projectId: string, month: string, val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    await updateUserProjectMonthlyPlan(projectId, month, num * 8.4);
  };

  const handleMonthlyTargetChange = async (month: string, val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    await updateMonthlyTarget(month, num);
  };

  const isTotalView = selectedProjectId === 'total';

  // Calculate the last reported month and unique logged days per month
  const lastReportedMonth = useMemo(() => {
    if (userEntries.length === 0) return null;
    let latest = userEntries[0].date;
    for (const e of userEntries) {
      if (e.date > latest) latest = e.date;
    }
    return latest.substring(0, 7);
  }, [userEntries]);

  const loggedDaysPerMonth = useMemo(() => {
    const acc: Record<string, Set<string>> = {};
    for (const e of userEntries) {
      const month = e.date.substring(0, 7);
      if (!acc[month]) acc[month] = new Set();
      acc[month].add(e.date);
    }
    return acc;
  }, [userEntries]);

  // Calculate row data for the selected view
  const rowData = months.map(month => {
    if (isTotalView) {
      let totalActualMins = 0;
      let totalEffectivePlannedDays = 0;

      projects.forEach(p => {
        if (actualsByProjectAndMonth[p.id]?.[month]) {
          totalActualMins += actualsByProjectAndMonth[p.id][month];
        }

        const pPlan = userProjectMonthlyPlans.find(b => b.projectId === p.id && b.month === month);
        if (pPlan) {
          totalEffectivePlannedDays += pPlan.plannedHours / 8.4;
        }
      });

      const totalActualDays = (totalActualMins / 60) / 8.4;
      const mTarget = monthlyTargets.find(t => t.month === month);
      const manualPlannedDays = mTarget ? mTarget.targetDays : 0;

      let diffDays = 0;
      if (lastReportedMonth && month === lastReportedMonth) {
        const loggedDays = loggedDaysPerMonth[month]?.size || 0;
        diffDays = totalActualDays - loggedDays;
      } else if (lastReportedMonth && month < lastReportedMonth) {
        diffDays = totalActualDays - manualPlannedDays;
      }

      return {
        month,
        planned: manualPlannedDays,
        plannedEffectively: totalEffectivePlannedDays,
        actual: totalActualDays,
        diff: diffDays,
        isOngoing: month === lastReportedMonth,
        isFuture: lastReportedMonth ? month > lastReportedMonth : true
      };
    } else {
      const pPlan = userProjectMonthlyPlans.find(b => b.projectId === selectedProjectId && b.month === month);
      const plannedDays = pPlan ? pPlan.plannedHours / 8.4 : 0;
      const actualMins = actualsByProjectAndMonth[selectedProjectId]?.[month] || 0;
      const actualDays = (actualMins / 60) / 8.4;

      let diffDays = 0;
      if (lastReportedMonth && month <= lastReportedMonth) {
        diffDays = actualDays - plannedDays;
      }

      return {
        month,
        planned: plannedDays,
        plannedEffectively: 0,
        actual: actualDays,
        diff: diffDays,
        isOngoing: month === lastReportedMonth,
        isFuture: lastReportedMonth ? month > lastReportedMonth : true
      };
    }
  });

  const totals = rowData.reduce((acc, row) => {
    acc.planned += row.planned;
    acc.plannedEffectively += row.plannedEffectively;
    acc.actual += row.actual;
    acc.diff += row.diff;
    return acc;
  }, { planned: 0, plannedEffectively: 0, actual: 0, diff: 0 });

  const getProjectName = (pId?: string) => {
    if (!pId) return 'Position';
    return projects.find(p => p.id === pId)?.name || 'Position';
  };

  const handleOpenNewBudget = () => {
    setEditingBudget(null);
    setIsBudgetModalOpen(true);
  };

  const handleEditBudget = (budget: ProjectBudget) => {
    setEditingBudget(budget);
    setIsBudgetModalOpen(true);
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            {isSupervisor ? 'Positions & Contingents' : 'Effort Tracker'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isSupervisor
              ? 'Track and manage positions, contracts, and team contingent limits.'
              : 'Track positions, contract limits, and monthly work.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isSupervisor && (
            <button
              onClick={handleOpenNewBudget}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-semibold shadow-sm hover:shadow transition cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              New Position
            </button>
          )}
          {!isSupervisor && (
            <select
              value={selectedProjectId}
              onChange={e => setSelectedProjectId(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm bg-white font-medium text-slate-700 min-w-[200px]"
            >
              <option value="total" className="font-bold">Total (All Positions)</option>
              <optgroup label="Positions">
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </optgroup>
            </select>
          )}
        </div>
      </div>

      {/* Shared Project Budgets Section */}
      {relevantBudgets.length > 0 ? (
        <div className="space-y-3">
          {/* Header & Controls Toolbar (Sticky) */}
          <div className="sticky top-16 z-20 bg-slate-50/95 backdrop-blur-md py-3 -mt-2 mb-4 border-b border-slate-200/80 shadow-xs space-y-2.5 transition-all">
            {/* Top row: Title, status filters, search input, expand/collapse, view layout */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-indigo-600" />
                  Active Positions & Contingents ({filteredBudgets.length}{filteredBudgets.length !== relevantBudgets.length && ` of ${relevantBudgets.length}`})
                </h2>

                {/* Status Filter Chips for Supervisor */}
                {isSupervisor && (
                  <div className="flex items-center gap-1 bg-slate-100/80 p-0.5 rounded-lg text-xs font-semibold ml-1">
                    <button
                      type="button"
                      onClick={() => setStatusFilter('all')}
                      className={`px-2.5 py-1 rounded-md transition cursor-pointer ${
                        statusFilter === 'all'
                          ? 'bg-white text-slate-900 shadow-xs'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      All ({relevantBudgets.length})
                    </button>
                    {supervisorOverview && supervisorOverview.needsAttentionCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setStatusFilter('attention')}
                        className={`px-2.5 py-1 rounded-md transition cursor-pointer flex items-center gap-1 ${
                          statusFilter === 'attention'
                            ? 'bg-amber-100 text-amber-900 shadow-xs font-bold'
                            : 'text-amber-700 hover:bg-amber-50'
                        }`}
                      >
                        Attention ({supervisorOverview.needsAttentionCount})
                      </button>
                    )}
                    {supervisorOverview && supervisorOverview.normalCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setStatusFilter('on-track')}
                        className={`px-2.5 py-1 rounded-md transition cursor-pointer ${
                          statusFilter === 'on-track'
                            ? 'bg-white text-emerald-800 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        On Track ({supervisorOverview.normalCount})
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Right: Search & Bulk Action Controls */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Employee Filter Dropdown for Supervisor */}
                {isSupervisor && (
                  <div className="relative min-w-[160px] sm:min-w-[200px]">
                    <Users className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <select
                      value={selectedEmployeeFilter}
                      onChange={e => setSelectedEmployeeFilter(e.target.value)}
                      aria-label="Filter positions by employee"
                      className={`w-full pl-8 pr-7 py-1.5 bg-white border rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-xs cursor-pointer appearance-none ${
                        selectedEmployeeFilter !== 'all'
                          ? 'border-indigo-300 bg-indigo-50/40 text-indigo-900 font-semibold'
                          : 'border-slate-200 text-slate-800'
                      }`}
                    >
                      <option value="all">All Employees</option>
                      {sortedProfiles.map(p => {
                        const name = [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email;
                        return (
                          <option key={p.id} value={p.id}>
                            {name}
                          </option>
                        );
                      })}
                    </select>
                    {selectedEmployeeFilter !== 'all' ? (
                      <button
                        type="button"
                        onClick={() => setSelectedEmployeeFilter('all')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                        title="Clear employee filter"
                        aria-label="Clear employee filter"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    )}
                  </div>
                )}

                {/* Search input */}
                <div className="relative min-w-[180px] sm:min-w-[220px]">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search positions..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-7 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-xs"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Expand / Collapse All */}
                <button
                  type="button"
                  onClick={handleToggleAll}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:text-indigo-600 hover:border-indigo-200 transition shadow-xs cursor-pointer"
                  title={allExpanded ? 'Collapse All' : 'Expand All'}
                >
                  <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400" />
                  <span>{allExpanded ? 'Collapse All' : 'Expand All'}</span>
                </button>

                {/* View Layout Switcher (List vs Grid) */}
                <div className="hidden sm:flex items-center bg-slate-100/80 p-0.5 rounded-lg border border-slate-200/60">
                  <button
                    type="button"
                    onClick={() => setLayoutMode('list')}
                    className={`p-1 rounded-md transition cursor-pointer ${layoutMode === 'list' ? 'bg-white text-indigo-600 shadow-xs' : 'text-slate-400 hover:text-slate-600'}`}
                    title="Dense List View"
                  >
                    <List className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setLayoutMode('grid')}
                    className={`p-1 rounded-md transition cursor-pointer ${layoutMode === 'grid' ? 'bg-white text-indigo-600 shadow-xs' : 'text-slate-400 hover:text-slate-600'}`}
                    title="Grid View"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom row: Grouping Controls */}
            <div className="flex items-center justify-between gap-2 flex-wrap pt-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">
                  <FolderTree className="w-3.5 h-3.5 text-slate-400" />
                  Group by:
                </span>
                <div className="inline-flex items-center bg-slate-100/80 p-0.5 rounded-lg text-xs font-semibold border border-slate-200/60">
                  <button
                    type="button"
                    onClick={() => handleGroupByChange('none')}
                    className={`px-2.5 py-1 rounded-md transition cursor-pointer ${
                      groupBy === 'none'
                        ? 'bg-white text-slate-900 shadow-xs'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    None
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGroupByChange('customerId')}
                    className={`px-2.5 py-1 rounded-md transition cursor-pointer flex items-center gap-1.5 ${
                      groupBy === 'customerId'
                        ? 'bg-indigo-600 text-white shadow-xs font-bold'
                        : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-200/50'
                    }`}
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    Customer Name
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGroupByChange('serviceType')}
                    className={`px-2.5 py-1 rounded-md transition cursor-pointer flex items-center gap-1.5 ${
                      groupBy === 'serviceType'
                        ? 'bg-indigo-600 text-white shadow-xs font-bold'
                        : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-200/50'
                    }`}
                  >
                    <Briefcase className="w-3.5 h-3.5" />
                    Service Type
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGroupByChange('contract')}
                    className={`px-2.5 py-1 rounded-md transition cursor-pointer flex items-center gap-1.5 ${
                      groupBy === 'contract'
                        ? 'bg-indigo-600 text-white shadow-xs font-bold'
                        : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-200/50'
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    SPF Contract ID
                  </button>
                </div>
              </div>

              {groupBy !== 'none' && (
                <div className="text-xs text-slate-500 font-medium">
                  {groupedBudgets.length} {groupedBudgets.length === 1 ? 'group' : 'groups'}
                </div>
              )}
            </div>
          </div>

          {/* Positions List / Grid (Flat or Grouped in Collapsible Segments) */}
          {filteredBudgets.length > 0 ? (
            groupBy !== 'none' ? (
              <div className="space-y-4">
                {groupedBudgets.map(group => {
                  const isCollapsed = collapsedGroupKeys.has(group.key);
                  return (
                    <div
                      key={group.key}
                      className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden transition-all"
                    >
                      {/* Group Header (Collapsible toggle) */}
                      <button
                        type="button"
                        onClick={() => toggleGroupCollapse(group.key)}
                        className="w-full px-4 py-3 bg-slate-50/90 hover:bg-slate-100/90 flex items-center justify-between border-b border-slate-200/70 transition cursor-pointer select-none text-left"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <ChevronDown
                            className={`w-4 h-4 text-slate-500 transition-transform duration-200 shrink-0 ${
                              isCollapsed ? '-rotate-90' : 'rotate-0'
                            }`}
                          />
                          {group.field === 'customerId' && <Building2 className="w-4 h-4 text-indigo-600 shrink-0" />}
                          {group.field === 'serviceType' && <Briefcase className="w-4 h-4 text-amber-600 shrink-0" />}
                          {group.field === 'contract' && <FileText className="w-4 h-4 text-blue-600 shrink-0" />}

                          <div className="flex items-baseline gap-2 truncate">
                            <span className={`text-sm font-bold truncate ${group.key === '__unassigned__' ? 'text-slate-400 italic' : 'text-slate-800'}`}>
                              {group.label}
                            </span>
                            <span className="text-xs text-slate-400 font-normal">
                              ({group.budgets.length} {group.budgets.length === 1 ? 'position' : 'positions'})
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 shrink-0 text-xs text-slate-500">
                          <span className="font-semibold text-slate-700">
                            {group.totalBookedDays.toFixed(1)}d booked
                            {group.totalContingentDays > 0 && (
                              <span className="text-slate-400 font-normal"> / {group.totalContingentDays.toFixed(1)}d</span>
                            )}
                          </span>
                        </div>
                      </button>

                      {/* Group Content (Cards) */}
                      {!isCollapsed && (
                        <div className="p-3.5 bg-slate-50/40">
                          <div className={layoutMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-3' : 'space-y-2.5'}>
                            {group.budgets.map(budget => (
                              <SharedBudgetCard
                                key={budget.id}
                                budget={budget}
                                projectName={getProjectName(budget.projectId || budget.id)}
                                entries={allAvailableEntries}
                                currentUserId={user?.id}
                                isSupervisor={isSupervisor}
                                profiles={profiles}
                                onEdit={handleEditBudget}
                                isCollapsed={!isPositionExpanded(budget.id)}
                                onToggleCollapse={() => handleTogglePosition(budget.id)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={layoutMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-3.5' : 'space-y-2.5'}>
                {filteredBudgets.map(budget => (
                  <SharedBudgetCard
                    key={budget.id}
                    budget={budget}
                    projectName={getProjectName(budget.projectId || budget.id)}
                    entries={allAvailableEntries}
                    currentUserId={user?.id}
                    isSupervisor={isSupervisor}
                    profiles={profiles}
                    onEdit={handleEditBudget}
                    isCollapsed={!isPositionExpanded(budget.id)}
                    onToggleCollapse={() => handleTogglePosition(budget.id)}
                  />
                ))}
              </div>
            )
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
              No positions match your current search or status filter.
            </div>
          )}
        </div>
      ) : isSupervisor ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
          <div className="bg-indigo-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Layers className="w-8 h-8 text-indigo-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No positions configured</h3>
          <p className="text-slate-500 max-w-sm mx-auto mb-6">
            Create a position to track team hours and contract limits.
          </p>
          <button
            onClick={handleOpenNewBudget}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-medium hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            New Position
          </button>
        </div>
      ) : null}

      {/* Summary Cards & Table (For non-supervisors) */}
      {!isSupervisor && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex items-start gap-4">
              <div className="bg-indigo-50 p-3 rounded-lg text-indigo-600">
                <Target className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                  {isTotalView ? 'Total Working Days' : 'Total Planned'}
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{totals.planned.toFixed(1)}d</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex items-start gap-4">
              <div className="bg-emerald-50 p-3 rounded-lg text-emerald-600">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Total Actual</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{totals.actual.toFixed(1)}d</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex items-start gap-4">
              <div className={`p-3 rounded-lg ${totals.diff > 0 ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'}`}>
                <AlertCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                  {isTotalView ? 'Total Overtime' : 'Difference'}
                </p>
                <p className={`text-2xl font-bold mt-1 ${totals.diff > 0 ? 'text-amber-600' : 'text-blue-600'}`}>
                  {totals.diff > 0 ? '+' : ''}{totals.diff.toFixed(1)}d
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto relative">
              <table className="w-full text-left border-collapse min-w-max">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-xs font-semibold text-slate-700 uppercase tracking-wider sticky left-0 bg-slate-50 border-r border-slate-200 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Metric</th>
                    {rowData.map(row => (
                      <th key={row.month} className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tracking-wider w-20">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{row.month.substring(0, 4)}</span>
                          <span className="text-sm">{format(parseISO(row.month + '-01'), 'MMM')}</span>
                        </div>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-xs font-bold text-slate-800 uppercase tracking-wider text-right bg-indigo-50/50 border-l border-slate-200 sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">Grand Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {/* Row 1: Working Days / Planned */}
                  <tr className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-slate-800 whitespace-nowrap sticky left-0 bg-white border-r border-slate-200 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      {isTotalView ? 'Working Days' : 'Planned (d)'}
                    </td>
                    {rowData.map(row => (
                      <td key={row.month} className="px-2 py-3 text-center">
                        {isTotalView ? (
                          <DebouncedInput
                            type="number"
                            step="0.1"
                            min="0"
                            value={row.planned || ''}
                            placeholder="0"
                            onChange={(val: string) => handleMonthlyTargetChange(row.month, val)}
                            className="w-16 px-1 py-1 text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none text-sm mx-auto"
                          />
                        ) : (
                          <DebouncedInput
                            type="number"
                            step="0.1"
                            min="0"
                            value={row.planned || ''}
                            placeholder="0"
                            onChange={(val: string) => handleBudgetChange(selectedProjectId, row.month, val)}
                            className="w-16 px-1 py-1 text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none text-sm mx-auto"
                          />
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-sm text-right font-bold text-slate-800 bg-indigo-50/50 border-l border-slate-200 sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      {totals.planned.toFixed(1)}
                    </td>
                  </tr>

                  {/* Row 2: Planned Effectively (Only if Total View) */}
                  {isTotalView && (
                    <tr className="hover:bg-slate-50/50 transition-colors bg-slate-50/30">
                      <td className="px-4 py-3 text-sm font-medium text-slate-500 whitespace-nowrap sticky left-0 bg-slate-50/90 border-r border-slate-200 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        Planned Effectively (d)
                      </td>
                      {rowData.map(row => (
                        <td key={row.month} className="px-2 py-3 text-sm text-center text-slate-500 font-medium">
                          {row.plannedEffectively.toFixed(1)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-sm text-right font-bold text-slate-500 bg-indigo-50/50 border-l border-slate-200 sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        {totals.plannedEffectively.toFixed(1)}
                      </td>
                    </tr>
                  )}

                  {/* Row 3: Actual */}
                  <tr className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-slate-800 whitespace-nowrap sticky left-0 bg-white border-r border-slate-200 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      Actual (d)
                    </td>
                    {rowData.map(row => (
                      <td key={row.month} className="px-2 py-3 text-sm text-center font-medium text-slate-700">
                        {row.actual.toFixed(1)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-sm text-right font-bold text-slate-800 bg-indigo-50/50 border-l border-slate-200 sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      {totals.actual.toFixed(1)}
                    </td>
                  </tr>

                  {/* Row 4: Difference */}
                  <tr className="hover:bg-slate-50/50 transition-colors bg-slate-50/30">
                    <td className="px-4 py-3 text-sm font-medium text-slate-800 whitespace-nowrap sticky left-0 bg-slate-50/90 border-r border-slate-200 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      Difference
                    </td>
                    {rowData.map(row => (
                      <td key={row.month} className="px-2 py-3 text-center">
                        {row.isFuture ? (
                          <span className="text-slate-400 font-medium">-</span>
                        ) : (
                          <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[11px] font-bold ${row.diff > 0 ? 'bg-amber-100 text-amber-800' : row.diff < 0 ? 'bg-blue-100 text-blue-800' : 'bg-slate-200 text-slate-700'}`} title={row.isOngoing && isTotalView ? "Calculated using logged days" : ""}>
                            {row.diff > 0 ? '+' : ''}{row.diff.toFixed(1)}{row.isOngoing && isTotalView && '*'}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className={`px-4 py-3 text-sm text-right font-bold bg-indigo-50/50 border-l border-slate-200 sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)] ${totals.diff > 0 ? 'text-amber-600' : totals.diff < 0 ? 'text-blue-600' : 'text-slate-600'}`}>
                      {totals.diff > 0 ? '+' : ''}{totals.diff.toFixed(1)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Supervisor Budget Modal */}
      {isSupervisor && (
        <BudgetEditModal
          isOpen={isBudgetModalOpen}
          onClose={() => {
            setIsBudgetModalOpen(false);
            setEditingBudget(null);
          }}
          onSave={saveProjectBudget}
          onDelete={deleteProjectBudget}
          budget={editingBudget}
          projects={projects}
          profiles={profiles}
          initialProjectId={selectedProjectId}
        />
      )}
    </div>
  );
}
